import { Button, Grid } from '@mui/material';
import {
  ControlledField,
  DateTimePickerInputField,
  OwnerType,
  ScheduleItemType,
  SelectInputField,
  getDateTimeShortFormatWithLocale,
} from 'remote/global';
import { useForm } from 'react-hook-form';
import { RestScheduleItemSchemaType, restScheduleItemSchema } from 'components/forms/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-hot-toast';
import {
  useOperatorsScheduleDriversQuery,
  usePostOperatorsScheduleMutation,
  usePutOperatorsScheduleMutation,
} from 'api/queries/operators';
import { DEFAULT_VACATION_TYPE } from 'utils/constants';
import { useTranslation } from 'react-i18next';

type Props = {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>; // Used to change the open state
  isEditMode?: boolean;
  defaultValues?: Partial<RestScheduleItemSchemaType>;
};

const vacationTypes = [
  ScheduleItemType.VACATION,
  ScheduleItemType.TIME_OFF,
  ScheduleItemType.SICKNESS,
];

function DriverVacationForm(props: Props) {
  const { setOpen, defaultValues, isEditMode } = props;
  const tInstance = useTranslation();
  const { t, i18n } = tInstance;
  const { data: schedule } = useOperatorsScheduleDriversQuery({}); // Make sure to pass empty object to activate 'enabled'
  const postScheduleMutation = usePostOperatorsScheduleMutation();
  const putScheduleMutation = usePutOperatorsScheduleMutation();

  const formInstance = useForm<RestScheduleItemSchemaType>({
    defaultValues: {
      itemType: DEFAULT_VACATION_TYPE,
      ...defaultValues,
      ownerType: OwnerType.DRIVER,
    },
    resolver: zodResolver(restScheduleItemSchema),
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValidating, isLoading },
  } = formInstance;

  const handleCloseModal = () => {
    setOpen(false);
  };

  const onValid = async (data: RestScheduleItemSchemaType) => {
    try {
      if (!isEditMode) {
        await postScheduleMutation.mutateAsync(data);
        toast.success(t('Toast.success'));
        handleCloseModal();
      } else {
        await putScheduleMutation.mutateAsync(data);
        toast.success(t('Toast.success'));
        handleCloseModal();
      }
    } catch (err) {
      toast.error(t('Toast.failure'));
    }
  };

  const isOwnerIdPreselected = typeof defaultValues?.ownerId === 'number';
  const driverCandidates =
    schedule?.drivers?.map((candidate) => {
      return {
        label: `${candidate.firstName} ${candidate.lastName}`,
        value: candidate.id,
      };
    }) ?? [];

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Grid container p={3} gap={3}>
        <Grid container item xs={12} spacing={1}>
          <Grid item xs={12} md={6}>
            <ControlledField
              control={control}
              name='ownerId'
              render={(renderProps) => (
                <SelectInputField
                  {...renderProps}
                  options={driverCandidates}
                  translationKey='restScheduleItem.ownerId'
                  fieldProps={{
                    disabled: isOwnerIdPreselected,
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ControlledField
              control={control}
              name='itemType'
              render={(renderProps) => (
                <SelectInputField
                  {...renderProps}
                  translationKey='restScheduleItem.itemType'
                  options={vacationTypes?.map((value) => {
                    return {
                      label: t(`DriverStatus.${value}`),
                      value,
                    };
                  })}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container item spacing={1}>
          <Grid item xs={12} md={6}>
            <ControlledField
              control={control}
              name='startTime'
              render={(renderProps) => (
                <DateTimePickerInputField
                  {...renderProps}
                  translationKey='restScheduleItem.startTime'
                  fieldProps={{
                    format: getDateTimeShortFormatWithLocale(i18n.language),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ControlledField
              control={control}
              name='endTime'
              render={(renderProps) => (
                <DateTimePickerInputField
                  {...renderProps}
                  translationKey='restScheduleItem.endTime'
                  fieldProps={{
                    format: getDateTimeShortFormatWithLocale(i18n.language),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} textAlign='right'>
          <Button
            type='submit'
            variant='contained'
            disabled={isSubmitting || isValidating || isLoading}
            fullWidth
            sx={{ mt: '2rem' }}
          >
            {t('confirm')}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

export default DriverVacationForm;
